import { Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material"
import { ChevronRightOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"

import { OPPORTUNITY_PIPELINE_TAB } from "../constants"
import { useSalesOpportunityList } from "../hooks/useSalesOpportunityList"

import { NewRequestDialog } from "./NewRequestDialog/NewRequestDialog"
import { SalesOpportunityCard } from "./SalesOpportunityCard/SalesOpportunityCard"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"

export const SalesMobilePage: FC = () => {
  const [isNewRequestOpen, setIsNewRequestOpen] = useState(false)

  const {
    salesOpportunities,
    isFetching,
    error,
    searchParams,
    tableContainerRef,
    refetch,
    handleSearchChange,
    handleScroll,
  } = useSalesOpportunityList({ route: "/app/tablet/sales/" })

  const handleNewRequestOpen = () => setIsNewRequestOpen(true)
  const handleNewRequestClose = () => setIsNewRequestOpen(false)
  const handleNewRequestSubmit = () => {
    setIsNewRequestOpen(false)
    refetch()
  }

  const hasOpportunities = salesOpportunities.length > 0
  const isInitialLoading = isFetching && !hasOpportunities

  return (
    <Box className='flex h-full flex-col gap-y-4 p-4'>
      <Typography variant='h5' className='font-normal text-gray-900'>
        Sales
      </Typography>

      <Box>
        <Tabs value={OPPORTUNITY_PIPELINE_TAB}>
          <Tab label='Opportunity Pipeline' value={OPPORTUNITY_PIPELINE_TAB} />
        </Tabs>
      </Box>

      <SearchInput
        placeholder='Search by Company Name, ID'
        className='w-full'
        defaultValue={searchParams.query ?? ""}
        onChange={(e) => handleSearchChange(e.target.value)}
        data-testid='sales.mobile-page.search-field'
      />

      <Button
        appearance='contained'
        endIcon={<ChevronRightOutlined />}
        onClick={handleNewRequestOpen}
        className='w-full'
        data-testid='sales.mobile-page.start-request-button'
      >
        Start Request
      </Button>

      <Box
        ref={tableContainerRef}
        onScroll={handleScroll}
        className='flex min-h-0 flex-1 flex-col gap-y-3 overflow-y-auto'
        data-testid='sales.mobile-page.card-list'
      >
        {error && (
          <Typography color='error' data-testid='sales.mobile-page.error'>
            {error.message}
          </Typography>
        )}

        {!error &&
          hasOpportunities &&
          salesOpportunities.map((salesOpportunity) => (
            <SalesOpportunityCard key={salesOpportunity.id} salesOpportunity={salesOpportunity} />
          ))}

        {!error && isInitialLoading && (
          <Box className='flex justify-center py-6'>
            <CircularProgress size={32} data-testid='sales.mobile-page.loading' />
          </Box>
        )}

        {!error && !isFetching && !hasOpportunities && (
          <InformationMissingAlert title='No opportunities found' data-testid='sales.mobile-page.empty-state' />
        )}

        {!error && isFetching && hasOpportunities && (
          <Box className='flex justify-center py-3'>
            <CircularProgress size={24} data-testid='sales.mobile-page.loading-more' />
          </Box>
        )}
      </Box>

      {isNewRequestOpen && (
        <NewRequestDialog fullScreen onClose={handleNewRequestClose} onSubmit={handleNewRequestSubmit} />
      )}
    </Box>
  )
}
